import React from "react";
import { CodeBlock } from "@ui/CodeBlock";

function DeveloperAPIReference() {
    return (
        <div className="max-w-3xl mx-auto py-10 px-4 sm:px-6 lg:py-12 lg:px-8">
            <h1 className="text-3xl font-bold text-gray-900  mb-8">
                API Reference
            </h1>

            <p className="text-gray-600  mb-8">
                This reference documents all available endpoints in the Agent UI
                API. All API requests should be made to the base URL:{" "}
                <code className="bg-gray-100  px-2 py-1 rounded">
                    https://api.agentui.ai
                </code>
            </p>

            <section className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900  mb-4">
                    Available Endpoints
                </h2>

                <div className="space-y-8">
                    <div>
                        <h3 className="text-xl font-semibold text-gray-900  mb-3">
                            Register Function
                        </h3>
                        <div className="bg-gray-100  p-4 rounded-md mb-4">
                            <p className="font-mono text-sm">
                                POST /function-groups/functions
                            </p>
                        </div>
                        <p className="text-gray-600  mb-4">
                            Registers a new function that can be called by the
                            Agent UI system.
                        </p>
                        <CodeBlock language="bash">
                            {`curl -X POST https://api.agentui.ai/function-groups/functions \\
  -H "Content-Type: application/json" \\
  -d '{
    "privateKey": "your-api-key",
    "path": "/api/my-function",
    "type": "OUTGOING",
    "method": "POST",
    "parameters": {
      "userId": {
        "type": "string",
        "description": "User identifier"
      }
    },
    "headers": {
        "Authorization": "Bearer your-api-key"
    },
    "contentType": "JSON",
    "requiresAuth": false,
    "timeout": 30000,
    "retryPolicy": null,
    "agentDataField": null,
    "aiInstructions": null
  }'`}
                        </CodeBlock>
                    </div>
                </div>
            </section>

            <section className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900  mb-4">
                    Parameters Reference
                </h2>

                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 ">
                        <thead className="bg-gray-50 ">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500  uppercase tracking-wider">
                                    Parameter
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500  uppercase tracking-wider">
                                    Type
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500  uppercase tracking-wider">
                                    Required
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500  uppercase tracking-wider">
                                    Description
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white  divide-y divide-gray-200 ">
                            <tr>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 ">
                                    privateKey
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 ">
                                    string
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 ">
                                    Yes
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-500 ">
                                    Your API key for authentication
                                </td>
                            </tr>
                            <tr>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 ">
                                    path
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 ">
                                    string
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 ">
                                    Yes
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-500 ">
                                    The endpoint path for your function
                                </td>
                            </tr>
                            <tr>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 ">
                                    type
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 ">
                                    string
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 ">
                                    No
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-500 ">
                                    Function type: "INCOMING", "OUTGOING", or
                                    "AUTHENTICATION" (default: "OUTGOING")
                                </td>
                            </tr>
                            <tr>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 ">
                                    method
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 ">
                                    string
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 ">
                                    Yes
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-500 ">
                                    HTTP method (GET, POST, PUT, DELETE)
                                </td>
                            </tr>
                            <tr>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 ">
                                    parameters
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 ">
                                    object
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 ">
                                    No
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-500 ">
                                    Function parameters specification
                                </td>
                            </tr>
                            <tr>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 ">
                                    headers
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 ">
                                    object
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 ">
                                    No
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-500 ">
                                    HTTP headers to be sent with the request
                                </td>
                            </tr>
                            <tr>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 ">
                                    contentType
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 ">
                                    string
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 ">
                                    No
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-500 ">
                                    Content type (default: "JSON")
                                </td>
                            </tr>
                            <tr>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 ">
                                    requiresAuth
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 ">
                                    boolean
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 ">
                                    No
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-500 ">
                                    Whether authentication is required (default:
                                    false)
                                </td>
                            </tr>
                            <tr>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 ">
                                    timeout
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 ">
                                    number
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 ">
                                    No
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-500 ">
                                    Request timeout in milliseconds (default:
                                    30000)
                                </td>
                            </tr>
                            <tr>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 ">
                                    retryPolicy
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 ">
                                    object
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 ">
                                    No
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-500 ">
                                    Retry configuration for failed requests
                                </td>
                            </tr>
                            <tr>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 ">
                                    agentDataField
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 ">
                                    object
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 ">
                                    No
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-500 ">
                                    Additional agent-specific data configuration
                                </td>
                            </tr>
                            <tr>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 ">
                                    aiInstructions
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 ">
                                    string
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 ">
                                    No
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-500 ">
                                    Natural language instructions for AI to
                                    handle this function. Can also be configured
                                    in the Agent UI dashboard.
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </section>

            <section className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900  mb-4">
                    Parameter Examples
                </h2>

                <div className="space-y-8">
                    <div>
                        <h3 className="text-xl font-semibold text-gray-900  mb-3">
                            Basic Parameters
                        </h3>
                        <CodeBlock language="javascript">
                            {`{
  "privateKey": "sk_live_123...",
  "path": "/api/user/profile",
  "type": "OUTGOING",
  "method": "GET"
}`}
                        </CodeBlock>
                    </div>

                    <div>
                        <h3 className="text-xl font-semibold text-gray-900  mb-3">
                            Function Parameters
                        </h3>
                        <CodeBlock language="javascript">
                            {`{
  "parameters": {
    "userId": {
      "type": "string",
      "description": "The user's unique identifier",
      "required": true
    },
    "fields": {
      "type": "array",
      "description": "Profile fields to return",
      "items": {
        "type": "string",
        "enum": ["name", "email", "avatar"]
      }
    }
  }
}`}
                        </CodeBlock>
                    </div>

                    <div>
                        <h3 className="text-xl font-semibold text-gray-900  mb-3">
                            Retry Policy
                        </h3>
                        <CodeBlock language="javascript">
                            {`{
  "retryPolicy": {
    "maxRetries": 3,
    "initialDelay": 1000,
    "maxDelay": 5000,
    "backoffMultiplier": 2,
    "retryableStatusCodes": [408, 500, 502, 503, 504]
  }
}`}
                        </CodeBlock>
                    </div>

                    <div>
                        <h3 className="text-xl font-semibold text-gray-900  mb-3">
                            Agent Data Field
                        </h3>
                        <CodeBlock language="javascript">
                            {`{
  "agentDataField": {
    "field": "user.fullName",
    "type": "string"
  }
}`}
                        </CodeBlock>
                    </div>

                    <div>
                        <h3 className="text-xl font-semibold text-gray-900  mb-3">
                            AI Instructions
                        </h3>
                        <CodeBlock language="javascript">
                            {`{
  "aiInstructions": "This endpoint retrieves a user's profile information. When calling this function:
  - Always include the userId parameter
  - The fields parameter is optional and can be used to filter returned data
  - Handle 404 responses by asking for a different userId
  - Use this when users ask about their profile or need to verify their information"
}`}
                        </CodeBlock>
                    </div>

                    <div>
                        <h3 className="text-xl font-semibold text-gray-900  mb-3">
                            Complete Example
                        </h3>
                        <CodeBlock language="javascript">
                            {`{
  "privateKey": "sk_live_123...",
  "path": "/api/user/profile",
  "type": "OUTGOING",
  "method": "GET",
  "parameters": {
    "userId": {
      "type": "string",
      "description": "The user's unique identifier",
      "required": true
    },
    "fields": {
      "type": "array",
      "description": "Profile fields to return",
      "items": {
        "type": "string",
        "enum": ["name", "email", "avatar"]
      }
    }
  },
  "contentType": "JSON",
  "requiresAuth": true,
  "timeout": 5000,
  "retryPolicy": {
    "maxRetries": 3,
    "initialDelay": 1000,
    "maxDelay": 5000,
    "backoffMultiplier": 2,
    "retryableStatusCodes": [408, 500, 502, 503, 504]
  },
  "agentDataField": {
    "field": "user.fullName",
    "type": "string"
  },
  "aiInstructions": "This endpoint retrieves a user's profile information. When calling this function:
  - Always include the userId parameter
  - The fields parameter is optional and can be used to filter returned data
  - Handle 404 responses by asking for a different userId
  - Use this when users ask about their profile or need to verify their information"
}`}
                        </CodeBlock>
                    </div>
                </div>
            </section>

            <section className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900  mb-4">
                    Response Codes
                </h2>
                <div className="space-y-4">
                    <p className="text-gray-600 ">
                        <span className="font-semibold">200 - OK:</span>{" "}
                        Function registered successfully
                    </p>
                    <p className="text-gray-600 ">
                        <span className="font-semibold">
                            400 - Bad Request:
                        </span>{" "}
                        Invalid parameters
                    </p>
                    <p className="text-gray-600 ">
                        <span className="font-semibold">
                            401 - Unauthorized:
                        </span>{" "}
                        Invalid API key
                    </p>
                    <p className="text-gray-600 ">
                        <span className="font-semibold">
                            500 - Server Error:
                        </span>{" "}
                        Internal server error
                    </p>
                </div>
            </section>
        </div>
    );
}

export default DeveloperAPIReference;
